import React, { useMemo, useState, useEffect } from 'react';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import ItemListHeading from './item/itemTable/ItemListHeading';
import ItemTableRow from './item/itemTable/ItemTableRow';
import VisibilityDropdown from './VisibilityDropdown';
import axios from 'axios';
import { builderSurfaceJsonContentHeader } from '../../common/utils/restApiHeaderUtils';
import CustomLoader from '../common/CustomLoader';
import { toast } from 'react-toastify';
import { drapcodeIcon } from '../../common/custom/icon-library';
import CustomReactPaginate from '../../common/custom/CustomReactPaginate';
import showAlert from '../../common/utils/alert';
import TooltipInfo from '../../common/docs/TooltipInfo';
import { description } from '../../common/utils/event_constants';

const CollectionItemsTable = ({
  fields,
  items,
  actions,
  selectedCollection,
  projectId,
  version,
  flag,
  iPlugins,
  s3Url,
  limit = 25,
  searchTerm,
  totalItems,
  totalPages,
  projectDetail,
}) => {
  const collectionName = selectedCollection.collectionName;
  const header = builderSurfaceJsonContentHeader(projectId);
  const [currentPage, setCurrentPage] = useState(0);
  const [data, setData] = useState(items);
  const [loading, setLoading] = useState(true);
  const [loadingForSorting, setLoadingForSorting] = useState(false);

  const [selectedRows, setSelectedRows] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(() =>
    fields.reduce((acc, field) => {
      acc[field.fieldName] = true;
      return acc;
    }, {}),
  );
  const [columnOrder, setColumnOrder] = useState(() => fields.map((field) => field.fieldName));
  const [sorting, setSorting] = useState([]);

  const handleDeleteSelected = async () => {
    const selectedUuids = Object.keys(selectedRows).filter((id) => selectedRows[id]);
    if (selectedUuids.length === 0) return;

    const preConfirm = async () => {
      try {
        await actions.bulkDeleteItems({
          projectId,
          version,
          collectionName,
          ids: selectedUuids,
        });

        toast.success(`${selectedUuids.length} item(s) deleted successfully!`);

        const updatedData = data.filter((item) => !selectedUuids.includes(item.uuid));
        setData(updatedData);
        setSelectedRows({});
      } catch (error) {
        console.error('Bulk delete failed:', error);
      }
    };

    showAlert(
      `You are about to delete ${selectedUuids.length} item(s). This action cannot be undone!`,
      preConfirm,
      'Yes, delete them!',
    );
  };
  const getItems = async () => {
    setLoadingForSorting(true);
    try {
      const sortField = sorting.length > 0 ? sorting[0].id : 'createdAt';
      const sortOrder = sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : 'desc';
      const url =
        `${process.env.REACT_APP_BUILDER_ENGINE}v${version}/items/${collectionName}?` +
        `sortField=${sortField}&sortOrder=${sortOrder}` +
        `&limit=${limit}&page=${currentPage}` +
        `${searchTerm ? `&searchTerm=${searchTerm}` : ''}`;
      const { data: response } = await axios.get(url, header);
      return (response && response.content) || [];
    } catch (error) {
      console.error('Error fetching sorted items:', error);
      return items;
    } finally {
      setLoadingForSorting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const sortedData = await getItems();
      setData(sortedData);
      setLoading(false);
    };
    fetchData();
  }, [sorting, currentPage, limit, items]);

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setCurrentPage(0);
  };

  useEffect(() => {
    const updatedFields = fields.map((field) => field.fieldName);
    const getSettingData = async () => {
      setLoading(true);
      try {
        const { data: settings } = await actions.getTableSettings({
          projectId,
          version,
          collectionName,
        });
        if (Array.isArray(settings) && settings.length > 0) {
          let updatedColumnOrder = updatedFields;
          let updatedColumnVisibility = fields.reduce((acc, field) => {
            acc[field.fieldName] = true;
            return acc;
          }, {});
          settings.forEach(({ colProp, colPropValue }) => {
            switch (colProp) {
              case 'columnVisibility':
                updatedColumnVisibility = {
                  ...updatedColumnVisibility,
                  ...(colPropValue || {}),
                };
                fields.forEach((field) => {
                  if (!updatedColumnVisibility.hasOwnProperty(field.fieldName)) {
                    updatedColumnVisibility[field.fieldName] = true;
                  }
                });
                setColumnVisibility(updatedColumnVisibility);
                break;
              case 'columnOrder':
                updatedColumnOrder = colPropValue
                  ? [...new Set([...colPropValue, ...updatedColumnOrder])]
                  : updatedColumnOrder;
                setColumnOrder(updatedColumnOrder);
                break;
              default:
                break;
            }
          });
        }
      } catch (error) {
        console.error('Error fetching table settings:', error);
      } finally {
        setLoading(false);
      }
    };
    getSettingData();
  }, [collectionName, projectId, version]);

  const columns = useMemo(
    () =>
      fields.map((field) => ({
        id: field.fieldName,
        accessorKey: field.fieldName,
        header: () => <ItemListHeading field={field} tanTable={true} />,
        cell: (info) => info.getValue(),
        enableSorting: true,
      })),
    [fields, columnOrder, columnVisibility, collectionName],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
    },
    initialState: {
      pagination: {
        pageSize: limit,
      },
    },
    sortDescFirst: false,
    onSortingChange: handleSortingChange,
    manualSorting: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
  });

  const onPageChange = (val) => {
    setCurrentPage(val.selected);
  };

  const handleDragStart = (e, columnId) => {
    e.dataTransfer.setData('columnId', columnId);
  };

  const toggleColumnVisibility = async (visibility) => {
    const updatedVisibility = {
      ...columnVisibility,
      ...visibility,
    };
    setColumnVisibility(updatedVisibility);
    try {
      await actions.updateTableSettings({
        projectId,
        version,
        name: collectionName,
        columnOrder,
        columnVisibility: updatedVisibility,
        type: 'COLLECTION_ITEM',
      });
      toast.success(`Column visibility updated successfully!`);
    } catch (error) {
      console.error('Failed to update column visibility:', error);
      toast.error('Failed to update column visibility.');
    }
  };

  const handleDrop = async (e, targetColumnId) => {
    const draggedColumnId = e.dataTransfer.getData('columnId');
    if (draggedColumnId === targetColumnId) return;
    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedColumnId);
    const targetIndex = newOrder.indexOf(targetColumnId);
    [newOrder[draggedIndex], newOrder[targetIndex]] = [
      newOrder[targetIndex],
      newOrder[draggedIndex],
    ];
    setColumnOrder(newOrder);
    try {
      await actions.updateTableSettings({
        projectId,
        version,
        name: collectionName,
        columnOrder: newOrder,
        columnVisibility,
        type: 'COLLECTION_ITEM',
      });
      toast.success(`Columns reordered successfully!`);
    } catch (error) {
      console.error('Failed to update column order:', error);
      toast.error('Failed to reorder columns.');
    }
  };

  const handleRowSelect = (itemId, isChecked) => {
    setSelectedRows((prev) => ({
      ...prev,
      [itemId]: isChecked,
    }));
  };

  if (loading) {
    return (
      <div className="text-center">
        <CustomLoader loading={loading} size="20px" color="#303453" />
      </div>
    );
  }
  const selectedCount = Object.values(selectedRows).filter(Boolean).length;
  // const isDisabled = selectedCount === 0;
  return (
    <div>
      <div className="mb-3">
        <div className="d-flex align-items-center justify-content-between w-100">
          <VisibilityDropdown
            columns={table.getAllLeafColumns()}
            toggleColumnVisibility={toggleColumnVisibility}
          />
          {selectedCount > 0 && (
            <button
              className="btn btn-theme-delete btn-md d-flex align-items-center rounded-3 fw-semibold py-2 px-2 mx-2"
              title="Delete Selected Items"
              onClick={handleDeleteSelected}
              style={{ cursor: flag ? 'pointer' : 'not-allowed' }} // disabled={!flag || selectedCount === 0}
            >
              <i className="wb wb-trash mr-2"></i>
              Delete Selected ({selectedCount})
              {flag ? null : (
                <>
                  &nbsp;
                  <TooltipInfo
                    title={description}
                    headerText={`Permission Disabled!`}
                    showHeader={true}
                    showArrow={true}
                  />
                </>
              )}
            </button>
          )}
        </div>
        <span className="small text-muted p-1 d-block mt-1">
          <i className={`wb ${drapcodeIcon('info')} text-primary`} aria-hidden="true"></i>&nbsp; You
          can show or hide table columns using the Column Visibility option and reorder the columns
          (swap) by dragging and dropping the column headers.
        </span>
      </div>

      <div className="table-responsive show_scrollbar mh-50vh h-auto">
        <table className="table table-bordered table-word-break table-hover">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                <th
                  style={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    backgroundColor: '#fff',
                    zIndex: 3,
                    width: '130px',
                  }}
                >
                  Select Items
                </th>
                {headerGroup.headers.map(
                  (header) =>
                    header.column.getIsVisible() && (
                      <th
                        key={header.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, header.id)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => handleDrop(e, header.id)}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{
                          position: 'sticky',
                          top: 0,
                          backgroundColor: '#fff',
                          zIndex: 2,
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="ms-2 ml-3">
                              {loadingForSorting
                                ? '⟳'
                                : {
                                    asc: (
                                      <i className="webconnect webconnect-arrow-up-square-fill"></i>
                                    ),
                                    desc: (
                                      <i className="webconnect webconnect-arrow-down-square-fill"></i>
                                    ),
                                  }[header.column.getIsSorted()] || (
                                    <i className="webconnect webconnect-arrow-down-up"></i>
                                  )}
                            </span>
                          )}
                        </div>
                      </th>
                    ),
                )}
                <th
                  style={{
                    position: 'sticky',
                    top: 0,
                    right: 0,
                    backgroundColor: '#fff',
                    zIndex: 999,
                    minWidth: '100px',
                    padding: '8px',
                    borderLeft: '1px solid #ddd',
                  }}
                >
                  Actions
                </th>
              </tr>
            ))}
          </thead>
          <tbody className="">
            {data.length === 0 ? (
              <tr>
                <td colSpan={fields.length + 2} className="pl-5 align-middle text-center">
                  No Data!
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const orderedFields =
                  columnOrder && columnOrder.length
                    ? columnOrder
                        .map((columnId) => {
                          const column = fields.find((field) => field.fieldName === columnId);
                          return column || null;
                        })
                        .filter(Boolean)
                    : [];
                return (
                  <ItemTableRow
                    key={row.id}
                    fields={orderedFields}
                    item={row.original}
                    actions={actions}
                    selectedCollection={selectedCollection}
                    projectId={projectId}
                    version={version}
                    flag={flag}
                    iPlugins={iPlugins}
                    s3Url={s3Url}
                    columnVisibility={columnVisibility}
                    isSelected={selectedRows[row.original.uuid] || false}
                    onRowSelect={handleRowSelect}
                    projectDetail={projectDetail}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-center align-items-center mt-3">
        {totalItems > limit && (
          <CustomReactPaginate
            totalPages={totalPages}
            pageRangeDisplayed={2}
            marginPagesDisplayed={1}
            page={currentPage}
            onPageChange={onPageChange}
          />
        )}
      </div>
    </div>
  );
};

export default CollectionItemsTable;
